import { customAlphabet } from 'nanoid';
import React, { useCallback, useEffect, useMemo, useReducer } from 'react';
import { Field, Form } from 'react-final-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { RouteComponentProps, withRouter } from 'react-router-dom';

import { Checkbox, Notification, Title } from '@xsolla/ui-core';
import { AlertExclamation } from '@xsolla/ui-icons';
import { useModal } from '@xsolla/ui-modal';

import {
  disableRSASecret,
  enableRSASecret,
  generateRSASecret,
  getHMACSecret,
  getRSASecret,
  LoginProjectConfiguration,
  putHMACSecret
} from '../../../api';
import { ChecklistItems } from '../../../api/configurations/checklist/types';
import { useConfig } from '../../../contexts/config';
import { useChecklist } from '../../../contexts/settings';
import { useNotifications } from '../../../hooks/useNotifications';
import { usePaths } from '../../../routes/routesHelper';
import { RouteParams } from '../../../routes/types';
import {
  CodeBlock,
  CopyHiddenInput,
  FieldInputNumber,
  FormFooter,
  Notice,
  Preloader,
  RadioGroup,
  ToNavigationError
} from '../../ui';
import { ExtraButton } from '../Oauth2Settings/style';
import ChangeKeyModal from './changeKeyModal';
import { init, initialState, signatureReducer } from './reducer';
import {
  BlockWrapper,
  HeavyFont,
  ModalContentBox,
  SignatureMethodChangeWrapper,
  Subtitle,
  TokenExtra,
  Wrapper,
  WrapperCodeEditor
} from './style';
import { ActionType, SignatureTypes } from './types';
import { insertNewLines, isLongTokenLifetime, isSaveDisabled, SECRET_ALPHABET } from './utils';
import { parseTokenLifetimeInput, tokenLifetimeValidator } from './validator';

type Props = RouteComponentProps & {
  currentProject: LoginProjectConfiguration | null;
  updateCurrentProject: (
    currentProject: LoginProjectConfiguration
  ) => Promise<LoginProjectConfiguration>;
};

const SignatureType = ({
  history,
  currentProject,
  updateCurrentProject,
  match
}: Props): JSX.Element => {
  const { navigationPath } = usePaths();
  const { projectId } = match.params as RouteParams;
  const { xsollaAnalytics } = useConfig();
  const { showErrorNotify, showSuccessNotify } = useNotifications();
  const intl = useIntl();

  const [state, dispatch] = useReducer(signatureReducer, initialState, () => init(currentProject));
  const { enableChecklistItem } = useChecklist(projectId);

  const [onOpenModal, onCloseModal] = useModal(
    () => (
      <ChangeKeyModal
        cancelClick={onCloseModal}
        deleteClick={submitData}
        selectedSignature={state.selectedSignature}
        fetching={state.saveDataFetching}
      />
    ),
    [
      state.newHMAC,
      state.newRSA,
      state.saveDataFetching,
      state.selectedSignature,
      state.tokenLifetime
    ]
  );

  const onClose = () =>
    currentProject && history.push({ pathname: navigationPath(currentProject.id) });

  useEffect(() => {
    if (currentProject) {
      Promise.all([getHMACSecret(currentProject.id), getRSASecret(currentProject.id)])
        .then(([hmac, rsa]) => {
          dispatch({
            type: ActionType.getDataSuccess,
            payload: {
              hmac: hmac.secret,
              rsa: rsa[0] || null,
              selectedSignature: rsa.length ? SignatureTypes.rsa : SignatureTypes.hmac
            }
          });
        })
        .catch(([errorHMAC, errorRSA]) =>
          dispatch({
            type: ActionType.getDataFail,
            payload: errorHMAC?.description || errorRSA?.description
          })
        );
    }
  }, []);

  useEffect(() => {
    if (currentProject && state.selectedSignature === SignatureTypes.rsa && !state.currRSA) {
      generateRSA();
    }
  }, [state.selectedSignature]);

  const renderExtraButton = useCallback(() => {
    return (
      <ExtraButton
        type="button"
        disabled={state.newKeyFetching}
        look="link"
        palette="brand"
        onClick={handleReset}>
        {intl.messages[`settings.signature.reset.${state.selectedSignature}.title`]}
      </ExtraButton>
    );
  }, [state.selectedSignature, state.newKeyFetching]);

  const currHMAC = useMemo(() => state.newHMAC || state.currHMAC, [state.currHMAC, state.newHMAC]);
  const currRSA = useMemo(() => state.newRSA || state.currRSA, [state.currRSA, state.newRSA]);
  const isKeyChanged = useMemo(() => state.newRSA || state.newHMAC, [state.newHMAC, state.newRSA]);

  const handleReset = async () => {
    if (state.selectedSignature === 'rsa') {
      await generateRSA();
      return;
    }

    const newKey = customAlphabet(SECRET_ALPHABET, 64)();

    dispatch({ type: ActionType.getNewHMAC, payload: newKey });
  };

  const handleChangeCheck = useCallback(() => {
    dispatch({ type: ActionType.submitAgreeLongToken, payload: !state.agreeLongToken });
  }, [state.agreeLongToken]);

  const generateRSA = useCallback(() => {
    if (currentProject) {
      dispatch({ type: ActionType.getNewRSAStart });

      generateRSASecret(currentProject.id)
        .then((res) => {
          dispatch({ type: ActionType.getNewRSASuccess, payload: res });
        })
        .catch((err) => {
          dispatch({ type: ActionType.getNewRSAFail, payload: err.description });
        });
    }
  }, []);

  const renderNotification = () => {
    if (state.getNewKeyError) {
      return (
        <Notification
          palette="danger"
          caption={state.getNewKeyError}
          icon={() => <AlertExclamation />}>
          {intl.formatMessage({ id: 'app.error.title' })}
        </Notification>
      );
    }

    if (state.newHMAC || state.newRSA) {
      return (
        <Notification
          palette="warning"
          caption={intl.formatMessage({ id: 'settings.signature.notification.caption' })}
          icon={() => <AlertExclamation />}>
          {intl.formatMessage({ id: 'settings.signature.notification.title' })}
        </Notification>
      );
    }

    return <></>;
  };

  const renderKeyFields = useMemo(() => {
    if (state.selectedSignature === 'rsa') {
      if (state.newKeyFetching) {
        return <Preloader />;
      }

      return (
        <>
          <div data-test-id="ht_newkey_notification">{renderNotification()}</div>

          {!state.getNewKeyError && (
            <WrapperCodeEditor data-test-id="web-key-rs256-jwt-signature">
              <CodeBlock
                disabled
                allowCopyCode
                mode={{ name: 'javascript', jsonld: true }}
                initialValue={insertNewLines(JSON.stringify(currRSA))}
                label={intl.formatMessage({
                  id: `settings.signature.rsa.${state.newRSA ? 'new.' : ''}label`
                })}
                extra={renderExtraButton()}
                height={220}
              />
            </WrapperCodeEditor>
          )}
        </>
      );
    }

    return (
      <>
        <div data-test-id="ht_hs256_notification">
          <Notification icon={Notice}>
            {intl.formatMessage({ id: 'settings.signature.notification' })}
          </Notification>
        </div>

        <div data-test-id="ht_newkey_notification">{renderNotification()}</div>

        <Wrapper data-test-id="secret-key-hs256-jwt-signature">
          <CopyHiddenInput
            title={intl.messages[`settings.signature.hmac.${state.newHMAC ? 'new.' : ''}label`]}
            value={currHMAC}
            extraCopyHandle={() => copyHandle()}
            extra={renderExtraButton()}
          />
        </Wrapper>
      </>
    );
  }, [currHMAC, currRSA, state.selectedSignature, state.newKeyFetching, state.getNewKeyError]);

  const submitData = async () => {
    if (currentProject) {
      dispatch({ type: ActionType.submitStart });

      xsollaAnalytics.sendClick('login_pa_module__signature', 'submit', {
        signature: state.selectedSignature
      });

      try {
        const requests: Promise<unknown>[] = [];

        if (state.tokenLifetime && currentProject.token_expiration_time !== state.tokenLifetime) {
          requests.push(
            updateCurrentProject({
              ...currentProject,
              token_expiration_time: state.tokenLifetime
            })
          );
        }

        if (state.selectedSignature === SignatureTypes.hmac) {
          requests.push(disableRSASecret(currentProject.id));
        }
        if (state.newHMAC) {
          requests.push(putHMACSecret(currentProject.id, state.newHMAC));
        }
        if (state.selectedSignature === SignatureTypes.rsa && state.newRSA) {
          requests.push(enableRSASecret(currentProject.id, state.newRSA.kid));
        }

        await Promise.all(requests);

        dispatch({ type: ActionType.submitSuccess });
        onCloseModal();
        showSuccessNotify();
      } catch (err) {
        showErrorNotify(err);
        dispatch({ type: ActionType.submitFail });
        xsollaAnalytics.sendClick('login_pa_module__signature', 'error', {
          error: err.description
        });
      }
    }
  };

  const handleSubmit = async () => {
    if (state.tokenLifetimeError) {
      return;
    }

    if (isKeyChanged || state.selectedSignature !== state.initialSignature) {
      onOpenModal();
      return;
    }

    await submitData();
  };

  const copyHandle = () => {
    enableChecklistItem(ChecklistItems.COPY_SECRET_KEY);
  };

  if (state.getDataFetching) {
    return (
      <SignatureMethodChangeWrapper>
        <Preloader />
      </SignatureMethodChangeWrapper>
    );
  }

  if (state.getDataError) {
    return <ToNavigationError error={state.getDataError} pageName="signature-settings_open" />;
  }

  return (
    <Form onSubmit={() => Promise.resolve(null)}>
      {() => {
        return (
          <>
            <SignatureMethodChangeWrapper>
              <Wrapper>
                <Title center indent="sm">
                  {intl.formatMessage({ id: 'settings.signature.title' })}
                </Title>
                <BlockWrapper>
                  <Subtitle>{intl.formatMessage({ id: 'settings.signature.subtitle' })}</Subtitle>

                  <Field name="signature-type" type="radio">
                    {(props) => (
                      <RadioGroup
                        name={props.input.name}
                        onChange={(event) => {
                          props.input.onChange(event);
                          dispatch({
                            type: ActionType.changeSelectedSignature
                          });
                        }}
                        defaultValue={state.selectedSignature}
                        options={Object.keys(SignatureTypes).map((methodName) => ({
                          label: intl.messages[`settings.signature.${methodName}.switch.title`],
                          description:
                            intl.messages[`settings.signature.${methodName}.switch.caption`],
                          radioValue: methodName,
                          dataTestId: `radio-switch-${methodName}`
                        }))}
                        id="radio-group-field-signature"
                      />
                    )}
                  </Field>

                  {renderKeyFields}

                  <FieldInputNumber
                    dataTestId="token-lifetime-section"
                    name="token_expiration_time"
                    hideArrows
                    subLabelCompact
                    label={intl.formatMessage({ id: 'settings.signature.token-lifetime.title' })}
                    value={state.tokenLifetime ?? ''}
                    onChange={(event) => {
                      const newTokenLifetime = event.target.value;
                      dispatch({
                        type: ActionType.changeTokenLifetime,
                        payload: parseTokenLifetimeInput(newTokenLifetime)
                      });
                      dispatch({
                        type: ActionType.changeTokenLifetimeError,
                        payload: tokenLifetimeValidator(newTokenLifetime)
                      });
                    }}
                    errorMessage={
                      state.tokenLifetimeError &&
                      intl.formatMessage({ id: state.tokenLifetimeError })
                    }
                    subLabel={
                      // eslint-disable-next-line react/jsx-wrap-multilines
                      <FormattedMessage
                        id="settings.signature.token-lifetime.description"
                        values={{
                          br: () => <br />
                        }}
                      />
                    }
                    extra={() => (
                      <TokenExtra data-test-id="seconds-token-lifetime">
                        {intl.formatMessage({ id: 'settings.signature.token-lifetime.seconds' })}
                      </TokenExtra>
                    )}
                    extraAlign="right"
                  />
                  {isLongTokenLifetime(state.tokenLifetime) && (
                    <ModalContentBox>
                      <Checkbox
                        checked={state.agreeLongToken}
                        // disabled={fetching}
                        onChange={handleChangeCheck}>
                        <HeavyFont>
                          {intl.formatMessage({ id: 'settings.signature.long-token-checkbox' })}
                        </HeavyFont>
                      </Checkbox>
                    </ModalContentBox>
                  )}
                </BlockWrapper>
              </Wrapper>
            </SignatureMethodChangeWrapper>

            <FormFooter
              onSubmit={handleSubmit}
              onCancel={onClose}
              disabled={isSaveDisabled(state)}
              fetching={state.saveDataFetching}
              submitButtonType="button"
            />
          </>
        );
      }}
    </Form>
  );
};

export default withRouter(SignatureType);
